'use client'

import AutoScroll from 'embla-carousel-auto-scroll'
import { Pause, Play } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { PolaroidCard } from '@/components/PolaroidCard'
import { PolaroidDetailDialog } from '@/components/PolaroidDetailDialog'
import { heroVideoPlaybackButtonClassName } from '@/components/Hero/HeroVideoBackground'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { cn } from '@/utilities/ui'

export type PolaroidGalleryItem = {
  id: string
  autoplayVideo?: boolean
  caption?: string
  captionAlign?: 'left' | 'center' | 'right'
  captionLine1?: string
  captionLine2?: string
  detailText?: string
  image: string
  imageAlt: string
  openDetailAriaLabel?: string
  playAriaLabel?: string
  title?: string
  videoMimeType?: string
  videoUrl?: string
}

export type PolaroidGalleryCarouselProps = {
  ariaLabel: string
  autoplayPauseLabel: string
  autoplayPlayLabel: string
  className?: string
  detailDialog: {
    closeLabel: string
  }
  items: PolaroidGalleryItem[]
  playLabel: string
  previousLabel: string
  nextLabel: string
}

type FigmaPolaroidLayout = {
  rotate: number
  x: number
  y: number
}

/** Figma polaroid positions recycled across all carousel cards. */
const FIGMA_POLAROID_LAYOUTS: FigmaPolaroidLayout[] = [
  { x: -289, y: 64, rotate: 5 },
  { x: -120, y: 48, rotate: -15 },
  { x: 133.3, y: 66.57, rotate: 5 },
  { x: 395.61, y: 94.04, rotate: -5 },
  { x: 649.74, y: 19.92, rotate: 5 },
  { x: 850.1, y: 25.19, rotate: 20 },
  { x: 1113, y: 64, rotate: 5 },
]

const FIGMA_CARD_WIDTH_PX = 312
const FIGMA_Y_BASELINE = 19.92
const FIGMA_AVG_HORIZONTAL_SPACING = 233.67
const FIGMA_DEFAULT_OVERLAP_MARGIN = FIGMA_AVG_HORIZONTAL_SPACING - FIGMA_CARD_WIDTH_PX
/** Scales how much cards overlap; lower = less overlap, higher = tighter spacing. */
const FIGMA_OVERLAP_FACTOR = 0.8
/** Pixels advanced per animation frame while auto-scrolling. */
const AUTO_SCROLL_SPEED = 0.75

/** Extra stacking per recycled Figma preset (card 5 above neighbors). */
const FIGMA_LAYOUT_Z_BOOST = [0, 0, 0, 0, 4, 0, 0]
/** Keeps in-card videos above overlapping photo polaroids. */
const VIDEO_CARD_Z_BOOST = 20

function getPolaroidLayout(index: number): FigmaPolaroidLayout {
  return FIGMA_POLAROID_LAYOUTS[index % FIGMA_POLAROID_LAYOUTS.length]!
}

function getPolaroidZIndex(index: number, hasVideo = false): number {
  const layoutIndex = index % FIGMA_POLAROID_LAYOUTS.length
  return 10 + index + FIGMA_LAYOUT_Z_BOOST[layoutIndex]! + (hasVideo ? VIDEO_CARD_Z_BOOST : 0)
}

function getPolaroidMarginLeft(index: number, previousIndex = index - 1): number {
  if (index <= 0 && previousIndex < 0) return 0

  const previous = getPolaroidLayout(previousIndex)
  const current = getPolaroidLayout(index)
  const spacing = current.x - previous.x

  if (spacing > 0 && spacing < 400) {
    return (spacing - FIGMA_CARD_WIDTH_PX) * FIGMA_OVERLAP_FACTOR
  }

  return FIGMA_DEFAULT_OVERLAP_MARGIN * FIGMA_OVERLAP_FACTOR
}

function getPolaroidRotate(index: number, itemsLength: number, isHovered: boolean): number {
  const layout = getPolaroidLayout(index)
  if (isHovered) return 0

  let rotate = layout.rotate

  if (index === 0 && itemsLength > 1) {
    const lastRotate = getPolaroidLayout(itemsLength - 1).rotate
    if (Math.sign(rotate) !== 0 && Math.sign(rotate) === Math.sign(lastRotate)) {
      rotate = -rotate
    }
  }

  return rotate
}

const carouselControlClassName =
  'top-1/2 z-50 size-10 -translate-y-1/2 rounded-full border-[1.5px] border-white/95 bg-black/50 text-white hover:bg-black/60 hover:text-white disabled:opacity-40'

const carouselPreviousClassName = cn(carouselControlClassName, 'left-2 lg:left-12')
const carouselNextClassName = cn(carouselControlClassName, 'right-2 lg:right-12')

const MOBILE_POLAROID_VIEWPORT = '(max-width: 1023px)'

function isMobilePolaroidViewport() {
  return window.matchMedia(MOBILE_POLAROID_VIEWPORT).matches
}

function scrollPolaroidSlideToCenter(api: CarouselApi, index: number) {
  if (!api) return

  const slide = api.slideNodes()[index]
  const viewport = api.rootNode()
  if (!slide || !viewport) return

  const card = slide.querySelector('article') ?? slide
  const cardRect = card.getBoundingClientRect()
  const viewportRect = viewport.getBoundingClientRect()
  const delta =
    cardRect.left + cardRect.width / 2 - (viewportRect.left + viewportRect.width / 2)

  if (Math.abs(delta) < 2) return

  api.internalEngine().scrollTo.distance(-delta, false)
}

export function PolaroidGalleryCarousel({
  ariaLabel,
  autoplayPauseLabel,
  autoplayPlayLabel,
  className,
  detailDialog,
  items,
  playLabel,
  previousLabel,
  nextLabel,
}: PolaroidGalleryCarouselProps) {
  const [activeItemId, setActiveItemId] = useState<string | null>(null)
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null)
  const [playingSlideKey, setPlayingSlideKey] = useState<string | null>(null)
  const [api, setApi] = useState<CarouselApi>()
  const [isAutoplaying, setIsAutoplaying] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  const slides = React.useMemo(
    () =>
      (items.length > 1 ? [...items, ...items] : items).map((item, index) => ({
        item,
        slideKey: `${item.id}-loop-${index}`,
      })),
    [items],
  )

  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const playButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const autoScrollPlugin = useRef(
    AutoScroll({
      playOnInit: false,
      speed: AUTO_SCROLL_SPEED,
      startDelay: 0,
      stopOnFocusIn: false,
      stopOnInteraction: true,
      stopOnMouseEnter: false,
    }),
  )

  const activeItem = items.find((item) => item.id === activeItemId)
  const activeTriggerRef = activeItemId
    ? { current: triggerRefs.current[activeItemId] ?? playButtonRefs.current[activeItemId] ?? null }
    : undefined

  const stopAutoplay = useCallback(() => {
    autoScrollPlugin.current.stop()
    setIsAutoplaying(false)
  }, [])

  const startAutoplay = useCallback(() => {
    if (prefersReducedMotion || items.length <= 1) return

    setPlayingSlideKey(null)
    autoScrollPlugin.current.play()
    setIsAutoplaying(true)
  }, [items.length, prefersReducedMotion])

  const toggleAutoplay = useCallback(() => {
    if (isAutoplaying) {
      stopAutoplay()
      return
    }

    startAutoplay()
  }, [isAutoplaying, startAutoplay, stopAutoplay])

  const handleOpenDetail = useCallback(
    (itemId: string) => {
      setPlayingSlideKey(null)
      stopAutoplay()
      setActiveItemId(itemId)
    },
    [stopAutoplay],
  )

  const handlePlayVideo = useCallback(
    (slideKey: string, slideIndex: number) => {
      stopAutoplay()
      setPlayingSlideKey(slideKey)

      if (!api || !isMobilePolaroidViewport()) return

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollPolaroidSlideToCenter(api, slideIndex)
        })
      })
    },
    [api, stopAutoplay],
  )

  const handleCloseDetail = useCallback(() => {
    setActiveItemId(null)
  }, [])

  useEffect(() => {
    const motionMedia = window.matchMedia('(prefers-reduced-motion: reduce)')

    const syncMotion = () => {
      const reduced = motionMedia.matches
      setPrefersReducedMotion(reduced)

      if (reduced) {
        stopAutoplay()
      }
    }

    syncMotion()
    motionMedia.addEventListener('change', syncMotion)

    return () => {
      motionMedia.removeEventListener('change', syncMotion)
    }
  }, [stopAutoplay])

  useEffect(() => {
    if (!api) return

    const handleAutoScrollPlay = () => setIsAutoplaying(true)
    const handleAutoScrollStop = () => setIsAutoplaying(false)

    api.on('autoScroll:play', handleAutoScrollPlay)
    api.on('autoScroll:stop', handleAutoScrollStop)

    return () => {
      api.off('autoScroll:play', handleAutoScrollPlay)
      api.off('autoScroll:stop', handleAutoScrollStop)
    }
  }, [api])

  useEffect(() => {
    if (!api || prefersReducedMotion || items.length <= 1) return

    startAutoplay()

    const handlePointerDown = () => {
      stopAutoplay()
    }

    api.on('pointerDown', handlePointerDown)

    return () => {
      api.off('pointerDown', handlePointerDown)
    }
  }, [api, items.length, prefersReducedMotion, startAutoplay, stopAutoplay])

  const handlePreviousClick = useCallback(() => {
    setPlayingSlideKey(null)
    stopAutoplay()
    api?.scrollPrev()
  }, [api, stopAutoplay])

  const handleNextClick = useCallback(() => {
    setPlayingSlideKey(null)
    stopAutoplay()
    api?.scrollNext()
  }, [api, stopAutoplay])

  const showAutoplayToggle = items.length > 1 && !prefersReducedMotion

  return (
    <>
      <div
        className={cn(
          'pointer-events-none relative z-10 w-full',
          className,
        )}
      >
        <Carousel
          aria-label={ariaLabel}
          className="pointer-events-auto relative z-10 w-full"
          opts={{
            align: 'start',
            containScroll: 'trimSnaps',
            dragFree: false,
            loop: true,
          }}
          plugins={[autoScrollPlugin.current]}
          setApi={setApi}
        >
          {showAutoplayToggle ? (
            <button
              aria-pressed={isAutoplaying}
              className={cn(
                heroVideoPlaybackButtonClassName,
                'pointer-events-auto absolute right-2 top-2 z-[60] lg:right-12 lg:top-4',
              )}
              data-gallery-interactive
              onClick={toggleAutoplay}
              type="button"
            >
              {isAutoplaying ? (
                <Pause aria-hidden className="size-4 shrink-0" />
              ) : (
                <Play aria-hidden className="size-4 shrink-0" />
              )}
              {isAutoplaying ? autoplayPauseLabel : autoplayPlayLabel}
            </button>
          ) : null}

          <CarouselPrevious
            aria-label={previousLabel}
            className={carouselPreviousClassName}
            data-gallery-interactive
            onClick={handlePreviousClick}
            size="icon"
            variant="outline"
          />
          <CarouselNext
            aria-label={nextLabel}
            className={carouselNextClassName}
            data-gallery-interactive
            onClick={handleNextClick}
            size="icon"
            variant="outline"
          />

          <CarouselContent
            className="relative -ml-4 min-h-[26rem] items-start pt-14 pl-4 pr-4 [--polaroid-y-scale:0.55] lg:min-h-[24rem] lg:pt-14 lg:pb-6 lg:pl-8 lg:pr-8 lg:[--polaroid-y-scale:1]"
            role="list"
            viewportClassName="overflow-x-clip overflow-y-clip lg:overflow-y-visible"
          >
            {slides.map(({ item, slideKey }, index) => {
              const layout = getPolaroidLayout(index)
              const marginLeft =
                index === 0 && slides.length > 1
                  ? getPolaroidMarginLeft(index, slides.length - 1)
                  : getPolaroidMarginLeft(index)
              const yOffset = layout.y - FIGMA_Y_BASELINE
              const isHovered = hoveredCardId === slideKey
              const isPlaying = playingSlideKey === slideKey
              const hasVideo = Boolean(item.playAriaLabel && item.videoUrl)
              const autoplayVideo =
                Boolean(hasVideo && item.autoplayVideo) && !prefersReducedMotion
              const rotate = getPolaroidRotate(index, slides.length, isHovered)
              const hasDetailContent = Boolean(item.detailText) && !hasVideo

              return (
                <CarouselItem
                  key={slideKey}
                  className={cn(
                    'group/polaroid relative basis-[min(88vw,19.5rem)] self-start pb-12 pl-4 hover:!z-[1000] focus-within:!z-[1000] lg:basis-[19.5rem] lg:pb-16',
                  )}
                  onBlurCapture={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setHoveredCardId((current) => (current === slideKey ? null : current))
                    }
                  }}
                  onFocusCapture={() => {
                    setHoveredCardId(slideKey)
                  }}
                  onMouseEnter={() => {
                    setHoveredCardId(slideKey)
                  }}
                  onMouseLeave={() => {
                    setHoveredCardId((current) => (current === slideKey ? null : current))
                  }}
                  role="listitem"
                  style={{
                    marginLeft: marginLeft === 0 ? undefined : marginLeft,
                    zIndex: getPolaroidZIndex(index, hasVideo),
                  }}
                >
                  <div
                    className={cn(
                      'relative flex justify-center px-2 transition-transform duration-300 ease-out motion-reduce:transform-none',
                    )}
                    style={{
                      ['--polaroid-y-offset' as string]: `${yOffset}px`,
                      transform: `translateY(calc(var(--polaroid-y-offset) * var(--polaroid-y-scale, 1))) rotate(${rotate}deg)`,
                    }}
                  >
                    <PolaroidCard
                      autoplayVideo={autoplayVideo}
                      caption={item.caption}
                      captionAlign={item.captionAlign}
                      captionLine1={item.captionLine1}
                      captionLine2={item.captionLine2}
                      className="relative z-10"
                      image={item.image}
                      imageAlt={item.imageAlt}
                      isVideoPlaying={isPlaying}
                      onOpenDetail={
                        hasDetailContent ? () => handleOpenDetail(item.id) : undefined
                      }
                      openDetailAriaLabel={
                        hasDetailContent
                          ? (item.openDetailAriaLabel ??
                            `Open details: ${item.title ?? item.imageAlt}`)
                          : undefined
                      }
                      onPlayClick={
                        hasVideo ? () => handlePlayVideo(slideKey, index) : undefined
                      }
                      onVideoEnded={() => {
                        setPlayingSlideKey((current) => (current === slideKey ? null : current))
                      }}
                      playAriaLabel={hasVideo ? item.playAriaLabel : undefined}
                      playButtonRef={(element) => {
                        playButtonRefs.current[item.id] = element
                      }}
                      playLabel={playLabel}
                      triggerRef={(element) => {
                        triggerRefs.current[item.id] = element
                      }}
                      videoMimeType={item.videoMimeType}
                      videoUrl={hasVideo ? item.videoUrl : undefined}
                    />
                  </div>
                </CarouselItem>
              )
            })}
          </CarouselContent>
        </Carousel>
      </div>

      <PolaroidDetailDialog
        closeLabel={detailDialog.closeLabel}
        detailText={activeItem?.detailText}
        image={activeItem?.image}
        imageAlt={activeItem?.imageAlt}
        onClose={handleCloseDetail}
        open={activeItemId !== null}
        restoreFocusRef={activeTriggerRef}
        title={activeItem?.title}
      />
    </>
  )
}
